import { pushService } from '@doneverse/data/services'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isStandalone as detectStandalone } from '@/lib/pwa'

export type PushStatus = 'loading' | 'unsupported' | 'insecure' | 'denied' | 'idle' | 'subscribed'

export interface UsePushSubscriptionResult {
  status: PushStatus
  subscription: PushSubscription | null
  error: string | null
  isBusy: boolean
  isIos: boolean
  isStandalone: boolean
  subscribe: () => Promise<void>
  unsubscribe: () => Promise<void>
  sendLocalTest: () => Promise<void>
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  const buffer = new ArrayBuffer(raw.length)
  const view = new Uint8Array(buffer)
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i)
  return view
}

function detectIos(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

export function usePushSubscription(): UsePushSubscriptionResult {
  const [status, setStatus] = useState<PushStatus>('loading')
  const [subscription, setSubscription] = useState<PushSubscription | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null)

  const isIos = detectIos()
  const isStandalone = detectStandalone()

  const refreshStatus = useCallback(async () => {
    const reg = registrationRef.current
    if (!reg) return

    if (Notification.permission === 'denied') {
      setStatus('denied')
      setSubscription(null)
      return
    }

    const existing = await reg.pushManager.getSubscription()
    if (existing) {
      setSubscription(existing)
      setStatus('subscribed')
    } else {
      setSubscription(null)
      setStatus('idle')
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function init() {
      if (typeof window === 'undefined') return
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setStatus('unsupported')
        return
      }
      if (!window.isSecureContext) {
        setStatus('insecure')
        return
      }

      try {
        const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
        await navigator.serviceWorker.ready
        if (cancelled) return
        registrationRef.current = reg
        await refreshStatus()
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Could not register service worker.')
        setStatus('unsupported')
      }
    }

    void init()
    return () => {
      cancelled = true
    }
  }, [refreshStatus])

  const subscribe = useCallback(async () => {
    setError(null)
    setIsBusy(true)
    try {
      const reg = registrationRef.current
      if (!reg) throw new Error('Service worker not ready.')

      if (isIos && !isStandalone) {
        throw new Error(
          'On iPhone or iPad, add this site to the Home Screen first, then reopen it from the installed icon.',
        )
      }

      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        await refreshStatus()
        throw new Error('Permission was not granted.')
      }

      let sub = await reg.pushManager.getSubscription()
      if (!sub) {
        const publicKey = await pushService.getVapidPublicKey()
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        })
      }

      await pushService.saveSubscription(sub.toJSON())
      setSubscription(sub)
      setStatus('subscribed')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not enable notifications.')
      await refreshStatus()
    } finally {
      setIsBusy(false)
    }
  }, [isIos, isStandalone, refreshStatus])

  const unsubscribe = useCallback(async () => {
    setError(null)
    setIsBusy(true)
    try {
      const reg = registrationRef.current
      if (!reg) return
      const existing = await reg.pushManager.getSubscription()
      if (!existing) {
        await refreshStatus()
        return
      }
      const endpoint = existing.endpoint
      await existing.unsubscribe()
      await pushService.removeSubscription(endpoint)
      await refreshStatus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not disable notifications.')
    } finally {
      setIsBusy(false)
    }
  }, [refreshStatus])

  const sendLocalTest = useCallback(async () => {
    setError(null)
    const reg = registrationRef.current
    if (!reg) {
      setError('Service worker not ready.')
      return
    }
    try {
      await reg.showNotification('Doneverse', {
        body: 'This is how alerts will appear on this device.',
        icon: '/icons/icon-192.png',
        badge: '/icons/badge-72.png',
        data: { url: '/notifications' },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not show test notification.')
    }
  }, [])

  return {
    status,
    subscription,
    error,
    isBusy,
    isIos,
    isStandalone,
    subscribe,
    unsubscribe,
    sendLocalTest,
  }
}
